/**
 * Property Comparison Dashboard - GLOBAL ON TAP
 * 
 * Multi-property analytics comparison with:
 * - Property selection (checkboxes, select all)
 * - Demographic comparison across properties
 * - Review Engine vs Check-In comparison
 * - Export functionality
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { comparisonAPI, platformAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import CommandBar from '../../components/CommandBar';
import { toast } from 'sonner';
import {
  Building2,
  BarChart3,
  TrendingUp,
  TrendingDown,
  Activity,
  Star,
  ClipboardCheck,
  Calendar,
  Download,
  CheckSquare,
  Square,
  Filter,
  Loader2,
  ArrowLeft,
  Users,
  Target,
  DollarSign,
} from 'lucide-react';
import { tLang } from '../../lib/langHelper';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LineChart,
  Line,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from 'recharts';

// Distinct color palette for comparison charts
const CHART_COLORS = {
  green: '#22c55e',    // Check-In / Surveys
  orange: '#f97316',   // Review Engine / Reviews  
  blue: '#3b82f6',     // Properties
  fuchsia: '#d946ef',  // Special metrics
  purple: '#8b5cf6',   // Secondary metrics
  cyan: '#06b6d4',     // Tertiary metrics
};

export default function PropertyComparisonDashboard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [comparing, setComparing] = useState(false);
  
  // Available hotels for selection
  const [hotels, setHotels] = useState([]);
  const [selectedHotels, setSelectedHotels] = useState([]);
  const [selectAll, setSelectAll] = useState(true);
  
  // Filters
  const [periodDays, setPeriodDays] = useState('30');
  const [engagementType, setEngagementType] = useState('combined');
  
  // Comparison results
  const [comparisonData, setComparisonData] = useState(null);
  const [platformValues, setPlatformValues] = useState(null);

  // Fetch accessible hotels on mount
  useEffect(() => {
    fetchAccessibleHotels();
    fetchPlatformValues();
  }, []);

  // Auto-compare when selection changes
  useEffect(() => {
    if (hotels.length > 0) {
      runComparison();
    }
  }, [selectedHotels, periodDays, engagementType]);

  const fetchAccessibleHotels = async () => {
    try {
      const response = await comparisonAPI.getAccessibleHotels();
      setHotels(response.data.hotels);
      // Select all by default
      setSelectedHotels(response.data.hotels.map(h => h.id));
    } catch (error) {
      toast.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlatformValues = async () => {
    try {
      const response = await platformAPI.getValues();
      setPlatformValues(response.data.values);
    } catch (error) {
      console.error('Failed to load platform values');
    }
  };

  const runComparison = async () => {
    if (selectedHotels.length === 0) {
      setComparisonData(null);
      return;
    }
    
    setComparing(true);
    try {
      const response = await comparisonAPI.compareProperties(
        selectedHotels,
        parseInt(periodDays),
        engagementType
      );
      setComparisonData(response.data);
    } catch (error) {
      toast.error('Failed to load comparison data');
    } finally {
      setComparing(false);
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedHotels([]);
    } else {
      setSelectedHotels(hotels.map(h => h.id));
    }
    setSelectAll(!selectAll);
  };

  const handleHotelToggle = (hotelId) => {
    setSelectedHotels(prev => {
      if (prev.includes(hotelId)) {
        const newSelection = prev.filter(id => id !== hotelId);
        setSelectAll(false);
        return newSelection;
      } else {
        const newSelection = [...prev, hotelId];
        setSelectAll(newSelection.length === hotels.length);
        return newSelection;
      }
    });
  };

  const handleExport = () => {
    if (!comparisonData) return;
    
    // Create CSV
    const headers = ['Property', 'City', 'Taps', 'Reviews', 'Surveys', 'Total', 'Avg Rating', 'Satisfaction', 'Conversion %'];
    const rows = comparisonData.comparison.map(p => [
      p.hotel_name,
      p.city,
      p.taps,
      p.reviews,
      p.surveys,
      p.total_responses,
      p.avg_rating,
      p.avg_satisfaction,
      p.conversion_rate
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `property_comparison_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success(tLang(i18n.language, 'Export successful', 'Export réussi', 'Exportación exitosa'));
  };

  // Calculate estimated revenue
  const calculateRevenue = (data) => {
    if (!data || !platformValues) return 0;
    const reviewRevenue = data.total_reviews * (platformValues.avg_revenue_per_review || 5);
    const surveyRevenue = data.total_surveys * (platformValues.avg_revenue_per_survey || 3);
    return (reviewRevenue + surveyRevenue).toFixed(2);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <CommandBar
        title={tLang(i18n.language, 'Property Comparison', 'Comparaison des Propriétés', 'Comparación de propiedades')}
        subtitle={tLang(i18n.language, 'Analyze and compare your properties', 'Analysez et comparez vos propriétés', 'Analyze and compare your properties')}
        actions={
          <div className="flex items-center space-x-2">
            <Select value={periodDays} onValueChange={setPeriodDays}>
              <SelectTrigger className="w-[140px]">
                <Calendar className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">{tLang(i18n.language, '7 days', '7 jours', '7 días')}</SelectItem>
                <SelectItem value="30">{tLang(i18n.language, '30 days', '30 jours', '30 días')}</SelectItem>
                <SelectItem value="90">{tLang(i18n.language, '90 days', '90 jours', '90 días')}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={engagementType} onValueChange={setEngagementType}>
              <SelectTrigger className="w-[160px]">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="combined">{tLang(i18n.language, 'All', 'Tout', 'Todo')}</SelectItem>
                <SelectItem value="reviews">{tLang(i18n.language, 'Review Engine', 'Review Engine', 'Review Engine')}</SelectItem>
                <SelectItem value="surveys">{tLang(i18n.language, 'Check-In', 'Check-In', 'Check-In')}</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={handleExport} disabled={!comparisonData}>
              <Download className="w-4 h-4 mr-2" />
              {tLang(i18n.language, 'Export', 'Exporter', 'Exportar')}
            </Button>
          </div>
        }
      />

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        {/* Back button */}
        <button
          onClick={() => navigate('/hotels')}
          className="flex items-center text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {tLang(i18n.language, 'Back to Dashboard', 'Retour au Tableau de Bord', 'Volver al panel')}
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Property Selection Panel */}
          <Card className="bg-card border-border lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-foreground flex items-center justify-between">
                <span className="flex items-center">
                  <Building2 className="w-5 h-5 mr-2" />
                  {tLang(i18n.language, 'Properties', 'Propriétés', 'Propiedades')}
                </span>
                <span className="text-sm font-normal text-muted-foreground">
                  {selectedHotels.length}/{hotels.length}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Select All */}
              <div 
                className="flex items-center space-x-3 p-2 rounded-lg bg-secondary cursor-pointer hover:bg-secondary/80"
                onClick={handleSelectAll}
              >
                <Checkbox 
                  checked={selectAll} 
                  onCheckedChange={handleSelectAll}
                />
                <span className="font-medium text-foreground">
                  {tLang(i18n.language, 'Select All', 'Sélectionner Tout', 'Seleccionar todo')}
                </span>
              </div>
              
              <div className="border-t border-border my-2"></div>
              
              {/* Individual Hotels */}
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {hotels.map(hotel => (
                  <div 
                    key={hotel.id}
                    className={`flex items-center space-x-3 p-2 rounded-lg cursor-pointer transition-colors ${
                      selectedHotels.includes(hotel.id) ? 'bg-primary/10' : 'hover:bg-secondary'
                    }`}
                    onClick={() => handleHotelToggle(hotel.id)}
                  >
                    <Checkbox 
                      checked={selectedHotels.includes(hotel.id)}
                      onCheckedChange={() => handleHotelToggle(hotel.id)}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{hotel.name}</p>
                      <p className="text-xs text-muted-foreground">{hotel.city || 'N/A'}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Main Content Area */}
          <div className="lg:col-span-3 space-y-6">
            {comparing ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : comparisonData ? (
              <>
                {/* Summary Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="bg-card border-border">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground">
                            {tLang(i18n.language, 'Properties', 'Propriétés', 'Propiedades')}
                          </p>
                          <p className="text-2xl font-bold text-foreground">{comparisonData.totals.total_hotels}</p>
                        </div>
                        <Building2 className="w-8 h-8 text-blue-500" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-card border-border">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground">
                            {tLang(i18n.language, 'Total Taps', 'Total Taps', 'Total de taps')}
                          </p>
                          <p className="text-2xl font-bold text-foreground">{comparisonData.totals.total_taps}</p>
                        </div>
                        <Activity className="w-8 h-8 text-purple-500" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-card border-border">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground">
                            {tLang(i18n.language, 'Est. Revenue', 'Revenus Est.', 'Ingresos est.')}
                          </p>
                          <p className="text-2xl font-bold text-green-500">
                            ${calculateRevenue(comparisonData.totals)}
                          </p>
                        </div>
                        <DollarSign className="w-8 h-8 text-green-500" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-card border-border">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground">
                            {tLang(i18n.language, 'Avg Rating', 'Note Moy.', 'Calif. prom.')}
                          </p>
                          <p className="text-2xl font-bold text-amber-500">
                            ★ {comparisonData.totals.avg_rating || '-'}
                          </p>
                        </div>
                        <Star className="w-8 h-8 text-amber-500" />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Engagement Breakdown */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card className="bg-card border-border">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-foreground flex items-center text-base">
                        <Star className="w-4 h-4 mr-2 text-amber-500" />
                        Review Engine
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-foreground">{comparisonData.totals.total_reviews}</p>
                      <p className="text-sm text-muted-foreground">
                        {tLang(i18n.language, 'Reviews collected', 'Avis collectés', 'Reseñas recopiladas')}
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-card border-border">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-foreground flex items-center text-base">
                        <ClipboardCheck className="w-4 h-4 mr-2 text-green-500" />
                        Check-In
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-foreground">{comparisonData.totals.total_surveys}</p>
                      <p className="text-sm text-muted-foreground">
                        {tLang(i18n.language, 'Surveys completed', 'Sondages complétés', 'Encuestas completadas')}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Comparison Chart */}
                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="text-foreground">
                      {tLang(i18n.language, 'Property Comparison', 'Comparaison par Propriété', 'Comparación de propiedades')}
                    </CardTitle>
                    <CardDescription>
                      {tLang(i18n.language, 'Performance of each property', 'Performance de chaque propriété', 'Rendimiento de cada propiedad')}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[350px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={comparisonData.comparison.slice(0, 10)} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                          <XAxis type="number" className="text-muted-foreground" />
                          <YAxis 
                            dataKey="hotel_name" 
                            type="category" 
                            width={120}
                            className="text-muted-foreground"
                            tick={{ fontSize: 11 }}
                          />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: 'hsl(var(--card))', 
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px'
                            }}
                          />
                          <Legend />
                          {engagementType !== 'surveys' && (
                            <Bar 
                              dataKey="reviews" 
                              name={tLang(i18n.language, 'Review Engine', 'Review Engine', 'Review Engine')} 
                              fill={CHART_COLORS.orange}
                            />
                          )}
                          {engagementType !== 'reviews' && (
                            <Bar 
                              dataKey="surveys" 
                              name={tLang(i18n.language, 'Check-In', 'Check-In', 'Check-In')} 
                              fill={CHART_COLORS.green}
                            />
                          )}
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Detailed Table */}
                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="text-foreground">
                      {tLang(i18n.language, 'Property Details', 'Détails par Propriété', 'Detalles de propiedad')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="text-left py-3 px-4 text-muted-foreground font-medium">
                              {tLang(i18n.language, 'Property', 'Propriété', 'Propiedad')}
                            </th>
                            <th className="text-right py-3 px-4 text-muted-foreground font-medium">Taps</th>
                            <th className="text-right py-3 px-4 text-muted-foreground font-medium">
                              {tLang(i18n.language, 'Review Engine', 'Review Engine', 'Review Engine')}
                            </th>
                            <th className="text-right py-3 px-4 text-muted-foreground font-medium">Check-In</th>
                            <th className="text-right py-3 px-4 text-muted-foreground font-medium">
                              {tLang(i18n.language, 'Conversion', 'Conversion', 'Conversión')}
                            </th>
                            <th className="text-right py-3 px-4 text-muted-foreground font-medium">
                              {tLang(i18n.language, 'Rating', 'Note', 'Calificación')}
                            </th>
                            <th className="text-right py-3 px-4 text-muted-foreground font-medium">
                              {tLang(i18n.language, 'Trend', 'Tendance', 'Tendencia')}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {comparisonData.comparison.map((property, index) => (
                            <tr key={property.hotel_id} className="border-b border-border hover:bg-secondary/50">
                              <td className="py-3 px-4">
                                <div className="flex items-center space-x-3">
                                  <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center text-xs font-bold text-primary">
                                    {index + 1}
                                  </div>
                                  <div>
                                    <p className="font-medium text-foreground">{property.hotel_name}</p>
                                    <p className="text-xs text-muted-foreground">{property.city || 'N/A'}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="py-3 px-4 text-right text-foreground">{property.taps}</td>
                              <td className="py-3 px-4 text-right text-amber-500">{property.reviews}</td>
                              <td className="py-3 px-4 text-right text-green-500">{property.surveys}</td>
                              <td className="py-3 px-4 text-right">
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  property.conversion_rate >= 40 ? 'bg-green-500/20 text-green-500' :
                                  property.conversion_rate >= 20 ? 'bg-amber-500/20 text-amber-500' :
                                  'bg-red-500/20 text-red-500'
                                }`}>
                                  {property.conversion_rate}%
                                </span>
                              </td>
                              <td className="py-3 px-4 text-right text-amber-500">
                                {property.avg_rating > 0 ? `★ ${property.avg_rating}` : '-'}
                              </td>
                              <td className="py-3 px-4 text-right">
                                {property.conversion_rate >= 30 ? (
                                  <TrendingUp className="w-5 h-5 text-green-500 inline" />
                                ) : (
                                  <TrendingDown className="w-5 h-5 text-red-500 inline" />
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card className="bg-card border-border">
                <CardContent className="flex flex-col items-center justify-center py-16">
                  <BarChart3 className="w-16 h-16 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">
                    {tLang(i18n.language, 'Select Properties', 'Sélectionnez des propriétés', 'Seleccionar propiedades')}
                  </h3>
                  <p className="text-muted-foreground text-center max-w-md">
                    {tLang(i18n.language, 'Choose one or more properties from the left panel to view performance comparison.', 'Choisissez une ou plusieurs propriétés à gauche pour voir la comparaison des performances.', 'Choose one or more properties from the left panel to view performance comparison.')}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
